/** The charge-code APIs service-management-service serves side by side. */
export type ChargeCodeApi = "v1" | "v2";

const CHARGE_CODE_APIS: readonly ChargeCodeApi[] = ["v1", "v2"];

/** The API the UI spoke before the setting existed, so an unset value changes nothing. */
const DEFAULT_CHARGE_CODE_API: ChargeCodeApi = "v1";

/**
 * Reads `VITE_CHARGE_CODE_API`. An unknown value fails the build rather than picking an API the
 * deployment did not ask for.
 */
export const readChargeCodeApi = (raw: string | undefined): ChargeCodeApi => {
  if (raw === undefined || raw.trim() === "") {
    return DEFAULT_CHARGE_CODE_API;
  }
  const version = raw.trim().toLowerCase();
  if (!isChargeCodeApi(version)) {
    throw new Error(`VITE_CHARGE_CODE_API must be "v1" or "v2", got "${raw}"`);
  }
  return version;
};

const isChargeCodeApi = (value: string | null): value is ChargeCodeApi =>
  CHARGE_CODE_APIS.includes(value as ChargeCodeApi);

/**
 * The API the UI speaks: the version the user chose in this browser, when the stored value is one
 * the UI writes, else the build's default.
 */
export const resolveChargeCodeApi = (
  browserChoice: string | null,
  buildDefault: ChargeCodeApi,
): ChargeCodeApi => (isChargeCodeApi(browserChoice) ? browserChoice : buildDefault);

/**
 * The choice stored in this browser, honoured only in builds that show the Debug Panel's switch to make
 * it: elsewhere a value typed into storage by hand must not change the contract the UI speaks.
 */
export const honouredBrowserChoice = (
  stored: string | null,
  switchShown: boolean,
): string | null => (switchShown ? stored : null);

/**
 * The UI's own path prefixes for visit, billing and the report service, the services that serve both
 * contracts; the report service builds its charge-code reports from the contract it is called under.
 */
const CHARGE_CODE_SERVICE_PREFIXES: readonly string[] = [
  "/api/outpatient",
  "/api/billing",
  "/api/reports",
];

/** The segment naming v2 after a service's prefix, which the route mapping places under `api/v1`. */
const V2_SEGMENT = "/v2";

const isAtOrUnder = (url: string, prefix: string): boolean =>
  url === prefix || url.startsWith(`${prefix}/`) || url.startsWith(`${prefix}?`);

/**
 * A UI-relative visit, billing or report URL naming the charge-code API the UI speaks. The gateways route
 * only `api/v1`, so v2 travels as a segment right after the service's prefix, which the route
 * mapping turns into `api/v1/v2`: `/api/outpatient/visits` → `/api/outpatient/v2/visits`, sent to
 * `/outpatient-visit-service/api/v1/v2/visits`. A v1 request, and any other URL, is returned exactly
 * as it was.
 */
export const namingChargeCodeApi = (url: string, chargeCodeApi: ChargeCodeApi): string => {
  const servicePrefix = CHARGE_CODE_SERVICE_PREFIXES.find((prefix) => isAtOrUnder(url, prefix));
  if (chargeCodeApi === "v1" || servicePrefix === undefined) {
    return url;
  }
  return `${servicePrefix}${V2_SEGMENT}${url.slice(servicePrefix.length)}`;
};

/** The implementation of an API module for the charge-code API the UI speaks; both stay in the app. */
export const implementationFor = <T>(
  implementations: Record<ChargeCodeApi, T>,
  selected: ChargeCodeApi,
): T => implementations[selected];
